import moment from "moment-timezone";
import {
  createDefaultDateFilter,
  DEFAULT_CUSTOM_FROM_TIME,
  DEFAULT_CUSTOM_TO_TIME,
  LIDAR_DATE_PRESETS,
  resolveDateFilterRange,
} from "./lidarTelemetryDateFilter";
import {
  DISPLAY_DATETIME_FORMAT,
  getClientTimezone,
  parseUtcInstant,
} from "./dateTimeUtils";
import { formatTelemetryChartAxisTick } from "./telemetryChartAxis";
import { mergeChartZoomOptions } from "./chartZoomPlugin";

export { createDefaultDateFilter };

export const HISTOGRAM_BUCKET_OPTIONS = [
  { value: "1m", label: "1 minute" },
  { value: "5m", label: "5 minutes" },
  { value: "10m", label: "10 minutes" },
  { value: "30m", label: "30 minutes" },
  { value: "1h", label: "Hourly" },
  { value: "1d", label: "Daily" },
  { value: "1M", label: "Monthly" },
  { value: "3M", label: "3 months" },
  { value: "6M", label: "6 months" },
  { value: "1y", label: "Yearly" },
];

export const HISTOGRAM_DEFAULT_BUCKET = "5m";

export const EMPTY_HIERARCHY_FILTERS = {
  state: null,
  city: null,
  zone: null,
  ward: null,
  location: null,
  device: null,
};

export const HISTOGRAM_GROUP_BY_OPTIONS = [
  { value: "zone", label: "Zone" },
  { value: "ward", label: "Ward" },
  { value: "location", label: "Location" },
  { value: "device", label: "Device" },
];

const MS = {
  hour: 3_600_000,
  day: 86_400_000,
};

/** Mirrors backend `HISTOGRAM_BUCKET_SPECS.defaultRangeMs`. */
export const HISTOGRAM_BUCKET_DEFAULT_RANGE_MS = {
  "1m": 2 * MS.hour,
  "5m": 12 * MS.hour,
  "10m": 24 * MS.hour,
  "30m": 48 * MS.hour,
  "1h": 7 * MS.day,
  "1d": 90 * MS.day,
  "1M": 730 * MS.day,
  "3M": 5 * 365 * MS.day,
  "6M": 10 * 365 * MS.day,
  "1y": 20 * 365 * MS.day,
};

export function getHistogramBucketDefaultRangeMs(bucket = "1h") {
  return HISTOGRAM_BUCKET_DEFAULT_RANGE_MS[bucket] ?? 7 * MS.day;
}

export const HISTOGRAM_SHIFT_DAY_START = "08:00";
export const HISTOGRAM_SHIFT_NIGHT_START = "20:00";

function setLocalClock(base, hour, minute = 0) {
  return base.clone().hour(hour).minute(minute).second(0).millisecond(0);
}

/**
 * Default range follows the current shift:
 * 8 AM–8 PM if now is in that window, otherwise 8 PM–8 AM.
 */
export function createHistogramShiftDateFilter(
  nowInput = moment.tz(getClientTimezone()),
) {
  const now = moment.isMoment(nowInput)
    ? nowInput.clone()
    : moment.tz(nowInput, getClientTimezone());
  const hour = now.hour();

  let from;
  let to;
  if (hour >= 8 && hour < 20) {
    from = setLocalClock(now, 8);
    to = setLocalClock(now, 20);
  } else if (hour >= 20) {
    from = setLocalClock(now, 20);
    to = setLocalClock(now.clone().add(1, "day"), 8);
  } else {
    from = setLocalClock(now.clone().subtract(1, "day"), 20);
    to = setLocalClock(now, 8);
  }

  return {
    preset: LIDAR_DATE_PRESETS.custom,
    fromDate: from.toDate(),
    toDate: to.toDate(),
    fromTime: from.format("HH:mm"),
    toTime: to.format("HH:mm"),
  };
}

export function isHistogramFixedShiftWindow(dateFilter) {
  if (dateFilter?.preset !== LIDAR_DATE_PRESETS.custom) return false;
  const fromTime = dateFilter.fromTime ?? "";
  const toTime = dateFilter.toTime ?? "";
  return (
    (fromTime === HISTOGRAM_SHIFT_DAY_START &&
      toTime === HISTOGRAM_SHIFT_NIGHT_START) ||
    (fromTime === HISTOGRAM_SHIFT_NIGHT_START &&
      toTime === HISTOGRAM_SHIFT_DAY_START)
  );
}

/** Default reports period: current 8 AM–8 PM or 8 PM–8 AM shift. */
export function createHistogramDefaultDateFilter() {
  return createHistogramShiftDateFilter();
}

function emptyCustomFields() {
  return {
    fromDate: null,
    toDate: null,
    fromTime: DEFAULT_CUSTOM_FROM_TIME,
    toTime: DEFAULT_CUSTOM_TO_TIME,
  };
}

function createCustomDateFilterForSpan(spanMs) {
  const now = moment.tz(getClientTimezone());
  const from = now.clone().subtract(spanMs, "milliseconds");

  return {
    preset: LIDAR_DATE_PRESETS.custom,
    fromDate: from.toDate(),
    toDate: now.toDate(),
    fromTime: from.format("HH:mm"),
    toTime: now.format("HH:mm"),
  };
}

function presetCoversMinRange(preset, minMs) {
  const resolved = resolveDateFilterRange(
    preset,
    null,
    null,
    DEFAULT_CUSTOM_FROM_TIME,
    DEFAULT_CUSTOM_TO_TIME,
  );
  if (!resolved) return false;
  return resolved.endMsExclusive - resolved.startMs >= minMs;
}

/** Suggested date filter for a histogram interval. */
export function createDateFilterForBucket(bucket = "1h") {
  const minMs = getHistogramBucketDefaultRangeMs(bucket);
  const shiftFilter = createHistogramShiftDateFilter();
  const shiftResolved = resolveHistogramDateFilterRange(shiftFilter);
  const shiftSpanMs = shiftResolved
    ? shiftResolved.to.getTime() - shiftResolved.from.getTime()
    : 0;
  if (shiftSpanMs >= minMs) {
    return shiftFilter;
  }

  const presetCandidates = [
    LIDAR_DATE_PRESETS.today,
    LIDAR_DATE_PRESETS.yesterday,
    LIDAR_DATE_PRESETS.last7days,
    LIDAR_DATE_PRESETS.last30days,
    LIDAR_DATE_PRESETS.thisMonth,
  ];

  const matchedPreset = presetCandidates.find((preset) =>
    presetCoversMinRange(preset, minMs),
  );

  if (matchedPreset) {
    return { preset: matchedPreset, ...emptyCustomFields() };
  }

  return createCustomDateFilterForSpan(minMs);
}

/** Expand date filter when the current range is shorter than the interval needs. */
export function ensureDateFilterMeetsBucketMin(dateFilter, bucket = "1h") {
  const minMs = getHistogramBucketDefaultRangeMs(bucket);
  const resolved = resolveHistogramDateFilterRange(dateFilter);

  if (!resolved) {
    return createDateFilterForBucket(bucket);
  }

  const spanMs = resolved.to.getTime() - resolved.from.getTime();
  if (spanMs >= minMs) {
    return dateFilter;
  }

  if (
    dateFilter.preset === LIDAR_DATE_PRESETS.custom &&
    dateFilter.fromDate &&
    dateFilter.toDate
  ) {
    const toMoment = moment(resolved.to).subtract(1, "minute");
    const fromMoment = toMoment.clone().subtract(minMs, "milliseconds");

    return {
      preset: LIDAR_DATE_PRESETS.custom,
      fromDate: fromMoment.toDate(),
      toDate: toMoment.toDate(),
      fromTime: fromMoment.format("HH:mm"),
      toTime: toMoment.format("HH:mm"),
    };
  }

  return createDateFilterForBucket(bucket);
}

export function getHistogramPresetMinRangeMs(preset) {
  const resolved = resolveDateFilterRange(
    preset,
    null,
    null,
    DEFAULT_CUSTOM_FROM_TIME,
    DEFAULT_CUSTOM_TO_TIME,
  );
  if (!resolved) return 0;
  return resolved.endMsExclusive - resolved.startMs;
}

export function isHistogramPresetDisabled(preset, bucket) {
  if (!preset || preset === LIDAR_DATE_PRESETS.custom) return false;
  // Today is always selectable — it pairs with live chart updates.
  if (preset === LIDAR_DATE_PRESETS.today) return false;
  const minMs = getHistogramBucketDefaultRangeMs(bucket);
  return getHistogramPresetMinRangeMs(preset) < minMs;
}

/** Presets that re-resolve from calendar start (e.g. Today → extend to now). */
export function isHistogramLiveDatePreset(preset) {
  return preset === LIDAR_DATE_PRESETS.today;
}

/**
 * Whether Apply should keep auto-refresh Live mode.
 * Today, current 8–8 shift window, or sliding custom windows that end near now.
 */
export function shouldKeepHistogramLiveMode(dateFilter) {
  if (!dateFilter?.preset) return false;
  if (isHistogramLiveDatePreset(dateFilter.preset)) return true;
  if (dateFilter.preset !== LIDAR_DATE_PRESETS.custom) return false;

  const resolved = resolveHistogramDateFilterRange(dateFilter);
  if (!resolved?.from || !resolved?.to) return false;
  const now = Date.now();
  if (now >= resolved.from.getTime() && now <= resolved.to.getTime()) {
    return true;
  }
  const lagMs = now - resolved.to.getTime();
  return Number.isFinite(lagMs) && lagMs >= 0 && lagMs <= 2 * 60 * 1000;
}

/** Default histogram view: current 8 AM–8 PM / 8 PM–8 AM shift. */
export function getHistogramDefaultRange() {
  const resolved = resolveHistogramDateFilterRange(
    createHistogramDefaultDateFilter(),
  );
  if (resolved) return resolved;
  const now = moment.tz(getClientTimezone());
  const from = now.clone().hour(8).minute(0).second(0).millisecond(0);
  const to = now.clone().hour(20).minute(0).second(0).millisecond(0);
  return { from: from.toDate(), to: to.toDate(), periodLabel: "8 AM – 8 PM" };
}

/** @deprecated Use getHistogramDefaultRange */
export function getHistogramTodayRange() {
  return getHistogramDefaultRange();
}

export function resolveHistogramDateFilterRange(dateFilter) {
  if (!dateFilter?.preset) return null;

  const resolved = resolveDateFilterRange(
    dateFilter.preset,
    dateFilter.fromDate,
    dateFilter.toDate,
    dateFilter.fromTime,
    dateFilter.toTime,
  );

  if (!resolved) return null;

  let endMsExclusive = resolved.endMsExclusive;
  if (
    !isHistogramFixedShiftWindow(dateFilter) &&
    (dateFilter.preset === LIDAR_DATE_PRESETS.custom ||
      dateFilter.preset === LIDAR_DATE_PRESETS.today)
  ) {
    endMsExclusive = Math.min(endMsExclusive, Date.now());
  }
  if (endMsExclusive <= resolved.startMs) return null;

  return {
    from: new Date(resolved.startMs),
    to: new Date(endMsExclusive),
    periodLabel: resolved.periodLabel,
  };
}

export function formatHistogramBucketAxisLabel(
  bucket,
  bucketKey = "1h",
  spanMs = null,
) {
  const instant = parseUtcInstant(bucket);
  const local = instant?.local();
  if (!local) return String(bucket ?? "");

  if (spanMs != null && Number.isFinite(spanMs) && spanMs > 0) {
    return formatTelemetryChartAxisTick(instant.valueOf(), spanMs);
  }

  switch (bucketKey) {
    case "1m":
    case "5m":
    case "10m":
    case "30m":
      return local.format("hh:mm A");
    case "1h":
      return local.format("DD MMM hh:mm A");
    case "1d":
      return local.format("DD MMM");
    case "1M":
      return local.format("MMM YYYY");
    case "3M":
      return `Q${local.quarter()} ${local.format("YY")}`;
    case "6M":
      return local.month() < 6
        ? `H1 ${local.format("YY")}`
        : `H2 ${local.format("YY")}`;
    case "1y":
      return local.format("YYYY");
    default:
      return local.format("DD MMM YYYY");
  }
}

export function formatHistogramBucketTooltip(bucket, bucketKey = "1h") {
  const local = parseUtcInstant(bucket)?.local();
  if (!local) return String(bucket ?? "");

  switch (bucketKey) {
    case "1m":
    case "5m":
    case "10m":
    case "30m":
      return local.format("DD MMM YYYY, hh:mm A");
    case "1h":
      return local.format("DD MMM YYYY, hh:mm A");
    case "1d":
      return local.format("DD MMM YYYY");
    case "1M":
      return local.format("MMMM YYYY");
    case "3M": {
      const quarterStart = local.clone().startOf("quarter");
      const quarterEnd = local.clone().endOf("quarter");
      return `${quarterStart.format("DD MMM")} – ${quarterEnd.format("DD MMM YYYY")}`;
    }
    case "6M": {
      const year = local.year();
      if (local.month() < 6) {
        return `Jan – Jun ${year}`;
      }
      return `Jul – Dec ${year}`;
    }
    case "1y":
      return local.format("YYYY");
    default:
      return local.format(DISPLAY_DATETIME_FORMAT);
  }
}

export const histogramEntityLabel = (groupBy) => {
  if (groupBy === "ward") return "Ward";
  if (groupBy === "location") return "Location";
  if (groupBy === "device") return "Device";
  return "Zone";
};

export function getHistogramHierarchyLevels(groupBy) {
  if (groupBy === "device") return ["device"];
  if (groupBy === "ward") return ["state", "city", "zone", "ward"];
  if (groupBy === "location") {
    return ["state", "city", "zone", "ward", "location"];
  }
  return ["state", "city", "zone"];
}

const HIERARCHY_LEVEL_LABELS = {
  state: "State",
  city: "City",
  zone: "Zone",
  ward: "Ward",
  location: "Location",
  device: "Device",
};

export function validateHistogramHierarchy(
  groupBy,
  hierarchy = EMPTY_HIERARCHY_FILTERS,
) {
  const fieldErrors = getHistogramHierarchyFieldErrors(groupBy, hierarchy);
  const labels = Object.keys(fieldErrors).map(
    (level) => HIERARCHY_LEVEL_LABELS[level] || level,
  );

  if (!labels.length) return "";

  if (labels.length === 1) {
    return `${labels[0]} is required.`;
  }

  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]} are required.`;
}

export function getHistogramHierarchyFieldErrors(
  groupBy,
  hierarchy = EMPTY_HIERARCHY_FILTERS,
) {
  const errors = {};

  for (const level of getHistogramHierarchyLevels(groupBy)) {
    if (!hierarchy[level]?.value) {
      errors[level] = `${HIERARCHY_LEVEL_LABELS[level]} is required.`;
    }
  }

  return errors;
}

export function isHistogramHierarchyValid(
  groupBy,
  hierarchy = EMPTY_HIERARCHY_FILTERS,
) {
  return Object.keys(getHistogramHierarchyFieldErrors(groupBy, hierarchy)).length === 0;
}

export function resolveHistogramEntityId(groupBy, hierarchy = EMPTY_HIERARCHY_FILTERS) {
  if (groupBy === "device") return hierarchy.device?.value || "";
  if (groupBy === "ward") return hierarchy.ward?.value || "";
  if (groupBy === "location") return hierarchy.location?.value || "";
  return hierarchy.zone?.value || "";
}

export function appendHistogramHierarchyParams(params, hierarchy = EMPTY_HIERARCHY_FILTERS) {
  const next = new URLSearchParams(params || "");

  if (hierarchy.state?.value) next.set("stateId", hierarchy.state.value);
  if (hierarchy.city?.value) next.set("cityId", hierarchy.city.value);
  if (hierarchy.zone?.value) next.set("zoneId", hierarchy.zone.value);
  if (hierarchy.ward?.value) next.set("wardId", hierarchy.ward.value);
  if (hierarchy.location?.value) next.set("locationId", hierarchy.location.value);
  if (hierarchy.device?.value) next.set("deviceId", hierarchy.device.value);

  return next;
}

function toHierarchyOption(entry) {
  if (!entry?.id) return null;
  return {
    value: entry.id,
    label: entry.name || "Unassigned",
  };
}

export function hierarchyFiltersFromApi(entityHierarchy, groupBy) {
  if (!entityHierarchy) return null;

  const levels = getHistogramHierarchyLevels(groupBy);
  const source = {
    state: toHierarchyOption(entityHierarchy.state),
    city: toHierarchyOption(entityHierarchy.city),
    zone: toHierarchyOption(entityHierarchy.zone),
    ward: toHierarchyOption(entityHierarchy.ward),
    location: toHierarchyOption(entityHierarchy.location),
    device: toHierarchyOption(entityHierarchy.device),
  };

  const next = { ...EMPTY_HIERARCHY_FILTERS };
  for (const level of levels) {
    if (source[level]) next[level] = source[level];
  }

  return next;
}

export function hasHistogramHierarchySelection(groupBy, hierarchy = EMPTY_HIERARCHY_FILTERS) {
  return Boolean(resolveHistogramEntityId(groupBy, hierarchy));
}

export function isHistogramHierarchyEmpty(hierarchy = EMPTY_HIERARCHY_FILTERS) {
  return !(
    hierarchy.state?.value ||
    hierarchy.city?.value ||
    hierarchy.zone?.value ||
    hierarchy.ward?.value ||
    hierarchy.location?.value ||
    hierarchy.device?.value
  );
}

export function createDefaultHistogramFilters() {
  return {
    dateFilter: createHistogramDefaultDateFilter(),
    bucket: HISTOGRAM_DEFAULT_BUCKET,
    groupBy: "location",
    hierarchyFilters: { ...EMPTY_HIERARCHY_FILTERS },
  };
}

export const HISTOGRAM_CHART_COLORS = {
  rainfall: { fill: "#1D4ED8", border: "#1E3A8A" },
  waterLevel: { fill: "#312F91", border: "#1F1D5C" },
};

export function buildHistogramLineChartData(
  timeline,
  {
    label = "Rainfall",
    color = HISTOGRAM_CHART_COLORS.rainfall.fill,
    borderColor = HISTOGRAM_CHART_COLORS.rainfall.border,
    bucket = "1h",
    dateRange = null,
    valueKey = "value",
  } = {},
) {
  const points = timeline?.points ?? [];
  if (!points.length) return null;

  const spanMs =
    dateRange?.from && dateRange?.to
      ? Math.max(dateRange.to.getTime() - dateRange.from.getTime(), 0)
      : (() => {
          const times = points
            .map((point) => parseUtcInstant(point.bucket)?.valueOf())
            .filter((ms) => ms != null);
          if (times.length < 2) return 0;
          return Math.max(...times) - Math.min(...times);
        })();

  const dense = points.length > 120;
  const labels = points.map((point) =>
    formatHistogramBucketAxisLabel(point.bucket, bucket, spanMs),
  );

  return {
    labels,
    spanMs,
    pointCount: points.length,
    datasets: [
      {
        label,
        data: points.map((point) => point[valueKey] ?? 0),
        histogramBuckets: points.map((point) => point.bucket),
        borderColor,
        backgroundColor: color,
        fill: false,
        tension: 0.25,
        borderWidth: 2,
        pointRadius: dense ? 0 : 3,
        pointHoverRadius: 4,
        pointBackgroundColor: borderColor,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 1,
      },
    ],
  };
}

export const histogramLineChartOptions = (
  yTitle,
  bucket = "1h",
  spanMs = 0,
  pointCount = 0,
) =>
  mergeChartZoomOptions({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title(items) {
          const item = items[0];
          const rawBucket = item?.dataset?.histogramBuckets?.[item.dataIndex];
          return formatHistogramBucketTooltip(rawBucket, bucket);
        },
        label(context) {
          const value = context.parsed?.y ?? 0;
          return `${yTitle}: ${value}`;
        },
      },
    },
  },
  elements: {
    line: { tension: 0.25 },
    point: { radius: pointCount > 120 ? 0 : 3, hoverRadius: 4 },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { size: 10 },
        maxRotation: spanMs > 7 * MS.day ? 0 : 40,
        autoSkip: true,
        autoSkipPadding: 4,
        maxTicksLimit: pointCount > 500 ? 16 : pointCount > 120 ? 12 : 10,
      },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: yTitle, font: { size: 11 } },
      ticks: { font: { size: 10 } },
      grid: { color: "rgba(15, 23, 42, 0.08)" },
    },
  },
  });

export function buildSingleTimelineChartData(
  timeline,
  {
    label = "Rainfall",
    color = HISTOGRAM_CHART_COLORS.waterLevel.fill,
    borderColor = color,
    bucket = "1h",
    dateRange = null,
    valueKey = "value",
  } = {},
) {
  const points = timeline?.points ?? [];
  if (!points.length) return null;

  const spanMs =
    dateRange?.from && dateRange?.to
      ? Math.max(dateRange.to.getTime() - dateRange.from.getTime(), 0)
      : (() => {
          const times = points
            .map((point) => parseUtcInstant(point.bucket)?.valueOf())
            .filter((ms) => ms != null);
          if (times.length < 2) return 0;
          return Math.max(...times) - Math.min(...times);
        })();

  const dense = points.length > 120;
  const labels = points.map((point) =>
    formatHistogramBucketAxisLabel(point.bucket, bucket, spanMs),
  );

  return {
    labels,
    spanMs,
    pointCount: points.length,
    datasets: [
      {
        label,
        data: points.map((point) => {
          const raw = point[valueKey];
          if (raw == null) return 0;
          return raw;
        }),
        histogramBuckets: points.map((point) => point.bucket),
        backgroundColor: color,
        borderColor,
        borderWidth: 1,
        borderRadius: dense ? 0 : 3,
        maxBarThickness: dense ? undefined : 28,
        barPercentage: dense ? 1 : 0.82,
        categoryPercentage: dense ? 1 : 0.92,
      },
    ],
  };
}

export const cumulativeHistogramOptions = (
  yTitle,
  bucket = "1h",
  spanMs = 0,
  pointCount = 0,
) =>
  mergeChartZoomOptions({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title(items) {
          const item = items[0];
          const rawBucket = item?.dataset?.histogramBuckets?.[item.dataIndex];
          return formatHistogramBucketTooltip(rawBucket, bucket);
        },
        label(context) {
          const value = context.parsed?.y ?? 0;
          return `${yTitle}: ${value}`;
        },
      },
    },
  },
  datasets: {
    bar: {
      maxBarThickness: pointCount > 120 ? undefined : 28,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { size: 10 },
        maxRotation: spanMs > 7 * MS.day ? 0 : 40,
        autoSkip: true,
        autoSkipPadding: 4,
        maxTicksLimit: pointCount > 500 ? 16 : pointCount > 120 ? 12 : 10,
      },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: yTitle, font: { size: 11 } },
      ticks: { font: { size: 10 } },
      grid: { color: "rgba(15, 23, 42, 0.08)" },
    },
  },
  });

function peakFromWaterLevelPoints(points) {
  let peak = null;
  for (const point of points ?? []) {
    if (point?.value == null || !Number.isFinite(Number(point.value))) continue;
    const value = Number(point.value);
    peak = peak == null ? value : Math.max(peak, value);
  }
  return peak;
}

export function getHistogramPeriodSummary(histogramData) {
  const summary = histogramData?.summary;
  const summaryPeak = summary?.waterLevelPeakCm ?? summary?.waterLevelAvgCm;

  return {
    totalRainMm: summary?.rainfallTotalMm ?? null,
    peakWaterLevelCm:
      summaryPeak != null && Number.isFinite(Number(summaryPeak))
        ? Number(summaryPeak)
        : peakFromWaterLevelPoints(histogramData?.waterLevel?.points),
    confirmedDistPeakCm: summary?.confirmedDistPeakCm ?? null,
  };
}
